import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class;
import { computed } from 'mobx';
import moment from 'moment';
import { Model } from 'core/model/Model';
import { DEFAULT_DATETIME_FORMAT, timezone, formatDateTime } from 'core/util/dateUtils';
import { unavailableReferenceLabel } from 'app/stores/suppressions/suppressionReferences';
import { SUPPRESSION_LIFECYCLE_STATUS, SUPPRESSION_STATUS } from 'app/stores/suppressions/suppressionLifecycle';

/** Mirrors AlarmSilenceNotificationsDefinition in alarm.proto; stored exactly as it comes off the wire. */

/** Single reads and writes wrap the definition in a `silence` envelope; list responses do not. */
let SilenceModel = (_class = class SilenceModel extends Model {
  get urlRoot() {
    return '/api/ui/alertingManager/alarms/silence';
  }
  get isSilence() {
    return true;
  }

  /** Both models answer both questions so the mixed suppression/silence list can discriminate. */
  get isSuppression() {
    return false;
  }
  get dimensionToKeyPart() {
    return this.get('key')?.value ?? {};
  }
  get ruleId() {
    return this.get('rule_id');
  }
  get messages() {
    return {
      create: 'Alert silence successfully created. It will take effect in a few minutes.',
      update: 'Alert silence updated successfully',
      destroy: 'Alert silence removed successfully',
      duplicate: 'Alert silence duplicated successfully'
    };
  }
  get expired() {
    const expirationTime = this.get('end_time');
    return !expirationTime ? false : timezone.momentFn(expirationTime).isBefore(moment());
  }

  // Silences keep their own timing rules, but report the same status vocabulary as suppressions
  // because both are rendered by the shared list, filters and drawer.
  get lifecycleStatus() {
    if (this.expired) {
      return SUPPRESSION_LIFECYCLE_STATUS.EXPIRED;
    }
    return moment(this.get('start_time')).isAfter(moment()) ? SUPPRESSION_LIFECYCLE_STATUS.PLANNED : SUPPRESSION_LIFECYCLE_STATUS.ONGOING;
  }
  get invalidReferences() {
    const {
      $alerting
    } = this.store;
    const {
      ruleId
    } = this;
    if (!$alerting.policyCollection.hasFetched || $alerting.policyCollection.requestStatus || $alerting.policyCollection.error || !ruleId || $alerting.policyCollection.getPolicyByRuleId(ruleId)) {
      return [];
    }
    return [{
      type: 'policy',
      id: ruleId,
      label: unavailableReferenceLabel('policy')
    }];
  }
  get status() {
    return this.invalidReferences.length > 0 ? SUPPRESSION_STATUS.INVALID : this.lifecycleStatus;
  }
  get createdDay() {
    return formatDateTime(this.get('cdate'));
  }
  get expirationDetails() {
    const expirationTime = this.get('end_time');
    if (!expirationTime) {
      return {
        expirationDay: null,
        expired: false,
        duration: null
      };
    }
    const momentDate = moment(expirationTime);
    return {
      expirationDay: momentDate.format(DEFAULT_DATETIME_FORMAT),
      expired: this.expired,
      duration: moment.duration(momentDate.diff(moment())).humanize()
    };
  }
  get startTime() {
    return moment(this.get('start_time')).format(DEFAULT_DATETIME_FORMAT);
  }
  get startDetails() {
    const startTimeMoment = moment(this.get('start_time'));
    return {
      started: startTimeMoment.isSameOrBefore(moment()),
      futureStart: startTimeMoment.isAfter(moment()),
      diff: moment.duration(startTimeMoment.diff(moment())).humanize()
    };
  }
  get dimensions() {
    return Object.keys(this.dimensionToKeyPart);
  }
  get measurementDimension() {
    return this.dimensions.find(dimension => dimension.endsWith('km_measurement_name'));
  }
  get measurement() {
    const dimension = this.measurementDimension;
    return dimension ? this.dimensionToKeyPart[dimension] : undefined;
  }
  get userId() {
    return this.get('user_id');
  }

  /** Unwrap the `silence` envelope; the fields inside are already the stored shape. */
  deserialize(data = {}) {
    const {
      silence,
      ...rest
    } = data;
    return super.deserialize(silence ?? rest);
  }

  /** The gateway expects writes wrapped in a `silence` envelope; tolerate already-wrapped input. */
  serialize(data = {}) {
    const {
      silence,
      ...rest
    } = data;
    return super.serialize({
      silence: silence ?? rest
    });
  }
}, _applyDecoratedDescriptor(_class.prototype, "dimensionToKeyPart", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "dimensionToKeyPart"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "invalidReferences", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "invalidReferences"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "createdDay", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "createdDay"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dimensions", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "dimensions"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "measurementDimension", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "measurementDimension"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "measurement", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "measurement"), _class.prototype), _class);
export default SilenceModel;